import React, { useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    ActionButton,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Link,
    ProgressCircle,
    TextField,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import SupplierDroDown from "./formComponents/SupplierDropDown";
import { AIChatIcon } from "../../../../../components/protected/Icons/IconsLib";
import { Button } from "react-bootstrap";
import CustomerDroDown from "./formComponents/CustomerDropDown";
import UserDropDown from "./formComponents/UserDropDown";
import ContactDropDown from "./formComponents/ContactDropdown";

// ✅ added (same as supplier)
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";

interface IInvoiceCustomerSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

const InvoiceCustomerSection: React.FC<IInvoiceCustomerSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);

    const { addressTypes } = usePreloadAssets();
    const { store, mainService } = useDependency();

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const normalizeAddresses = (addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];

        let raw = addressesWrapper;

        // ADDRESSES: { ADDRESS: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }

        const list = Array.isArray(raw) ? raw : [raw];
        return list.map((x) => x?.ADDRESS ?? x).filter(Boolean);
    };

    const pickAddress = (
        entityType: "COMPANY" | "PERSON",
        addressesWrapper: any
    ) => {
        const list = normalizeAddresses(addressesWrapper);
        if (!list.length) return null;

        const isPrimary = (a: any) => String(a?.isPrimary) === "1";

        const matchByAddressTypes = (a: any) => {
            const typeId = String(a?.type ?? "");
            const at = (addressTypes as any)?.[typeId];
            if (!at) return false;

            return entityType === "COMPANY"
                ? String(at?.invoiceAddress) === "1"
                : String(at?.permanentAddress) === "1";
        };

        // 1) exact match
        const exact = list.find(matchByAddressTypes);
        if (exact) return exact;

        // 2) primary
        const primary = list.find(isPrimary);
        if (primary) return primary;

        // 3) index 0
        return list[0];
    };

    const handleCustomerChange = async (data: any) => {
        if (!data) {
            onChange({
                ...invoice,
                customer: "",
                customerName: "",
                customerStreet: "",
                customerCity: "",
                customerZIP: "",
                customerRegion: "",
                customerCountry: "",
            });
            return;
        }

        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                data.id
            )
        );

        if (response?.result !== "OK") return;

        const isPerson = !!response?.PERSON;
        const entity = isPerson ? response.PERSON : response.COMPANY;
        const entityType: "PERSON" | "COMPANY" = isPerson ? "PERSON" : "COMPANY";

        const address = pickAddress(entityType, entity?.ADDRESSES);

        onChange({
            ...invoice,
            customer: data.id,
            customerName: data?.name ?? entity?.name1 ?? entity?.name ?? "",

            customerStreet: address?.street ?? "",
            customerCity: address?.cityName ?? address?.city ?? "",
            customerZIP: address?.zipName ?? address?.zip ?? "",
            customerRegion: address?.regionName ?? address?.region ?? "",
            customerCountry: address?.countryName ?? address?.country ?? "",
        });
    };

    function CustomerModal() {
        return (
            <>
                <Dialog
                    width={{ L: "800px", base: "100%" }}
                    aria-label="Add Product"
                    isDismissable
                    onDismiss={() => setShowMorefields(false)}
                >
                    <Heading level={2}>{_t(isEdit ? "customer" : "customer")}</Heading>
                    <Content>
                        <Flex
                            direction={{ base: "column", L: "row" }}
                            gap={"size-150"}
                            marginTop={"size-150"}
                        >
                            <CustomerDroDown
                                selectedCustomer={invoice?.customer}
                                CustomerName={invoice?.customerName}
                                isEdit={isEdit}
                                onChange={(data) => {
                                    handleCustomerChange(data);
                                }}
                            />
                            <TextField
                                width={"100%"}
                                label="Street"
                                value={invoice?.customerStreet}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, customerStreet: e })
                                }
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <ContactDropDown
                                selectedContact={invoice?.customerContact}
                                contactName={invoice?.customerContactName}
                                isEdit={isEdit}
                                label={"contact"}
                                width={{ base: "100%", L: "52%" }}
                                onChange={(data) => {
                                    onChange({
                                        ...invoice,
                                        customerContact: data?.id,
                                        customerContactName: data?.name,
                                    });
                                }}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label="City"
                                    value={invoice?.customerCity}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerCity: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label="Zip Code"
                                    value={invoice?.customerZIP}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerZIP: e })
                                    }
                                />
                            </Flex>
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <TextField
                                width={{ base: "100%", L: "52%" }}
                                label="Name"
                                value={invoice?.customerName}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, customerName: e })
                                }
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label="Region"
                                    value={invoice?.customerRegion}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerRegion: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label="Country"
                                    value={invoice?.customerCountry}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerCountry: e })
                                    }
                                />
                            </Flex>
                        </Flex>
                    </Content>
                </Dialog>

                <Flex
                    UNSAFE_style={{
                        backgroundColor: "white",
                        justifyContent: "end",
                        width: "100%",
                        padding: "1rem",
                        paddingLeft: 55,
                        paddingRight: 50,
                        boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                    }}
                >
                    <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                        <Button
                            variant="danger"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_cancel}`}
                        >
                            {_tt("button_cancel")}
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_add}`}
                        >
                            {_tt("button_ok")}
                        </Button>
                    </Flex>
                </Flex>
            </>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    Show more
                </Link>
            </Flex>

            <CustomerDroDown
                selectedCustomer={invoice?.customer}
                CustomerName={invoice?.customerName}
                isEdit={isEdit}
                onChange={(data) => {
                    handleCustomerChange(data);
                }}
            />

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && CustomerModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceCustomerSection;
